import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, ArrowLeft, Plug, CheckCircle, XCircle, Phone, Mail, MessageSquare, Webhook, Image, RefreshCw, Loader2, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Hover Integration Card Component
const HoverIntegrationCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/integrations/hover/status`);
      setStatus(response.data);
    } catch (error) {
      console.error('Error fetching Hover status:', error);
      setStatus({ connected: false, error: 'Failed to fetch status' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleConnect = () => {
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    
    const popup = window.open(
      `${API}/integrations/hover/connect`,
      'hover-oauth',
      `width=${width},height=${height},left=${left},top=${top}`
    );

    const handleMessage = (event) => {
      if (event.data?.type === 'HOVER_CONNECTED') {
        popup?.close();
        fetchStatus();
        toast.success('Hover connected successfully!');
      }
    };
    window.addEventListener('message', handleMessage);

    const checkClosed = setInterval(() => {
      if (popup?.closed) {
        clearInterval(checkClosed);
        window.removeEventListener('message', handleMessage);
        fetchStatus();
      }
    }, 500);
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Hover?')) return;
    
    try {
      await axios.post(`${API}/integrations/hover/disconnect`);
      toast.success('Hover disconnected');
      fetchStatus();
    } catch (error) {
      toast.error('Failed to disconnect Hover');
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const response = await axios.get(`${API}/integrations/hover/test`);
      if (response.data.success) {
        toast.success('Hover connection test successful!');
      } else {
        toast.error(response.data.message || 'Connection test failed');
      }
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleRefresh = async () => {
    try {
      await axios.post(`${API}/integrations/hover/refresh`);
      toast.success('Token refreshed');
      fetchStatus();
    } catch (error) {
      toast.error('Failed to refresh token');
    }
  };

  if (loading) {
    return (
      <Card className="border-purple-200 dark:border-purple-800">
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-purple-200 dark:border-purple-800 bg-purple-50/30 dark:bg-purple-900/10">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
              <Image className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                Hover
                {status?.connected && status?.token_valid ? (
                  <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Connected
                  </Badge>
                ) : status?.connected && !status?.token_valid ? (
                  <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    Token Expired
                  </Badge>
                ) : (
                  <Badge variant="outline">
                    <XCircle className="h-3 w-3 mr-1" />
                    Not Connected
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                Property measurement and 3D modeling integration
              </CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {status?.connected && (
          <div className="space-y-2 mb-4 text-sm">
            <div>
              <span className="text-muted-foreground">Connected at:</span>
              <span className="ml-2">{status.connected_at ? new Date(status.connected_at).toLocaleString() : 'Unknown'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Token expires:</span>
              <span className="ml-2">{status.expires_at ? new Date(status.expires_at).toLocaleString() : 'Unknown'}</span>
            </div>
          </div>
        )}
        
        <div className="flex flex-wrap gap-2">
          {!status?.connected ? (
            <Button onClick={handleConnect} className="bg-purple-600 hover:bg-purple-700">
              <Plug className="h-4 w-4 mr-2" />
              Connect Hover
            </Button>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={handleTest} disabled={testing}>
                {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                Test Connection
              </Button>
              {!status.token_valid && (
                <Button variant="outline" size="sm" onClick={handleRefresh}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh Token
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleDisconnect} className="text-red-500 hover:text-red-600">
                <XCircle className="h-4 w-4 mr-2" />
                Disconnect
              </Button>
            </>
          )}
          <Button variant="ghost" size="sm" asChild>
            <a href="https://hover.to" target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4 mr-2" />
              Go to Hover
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const IntegrationsSettings = () => {
  const navigate = useNavigate();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'api',
    api_key: '',
    api_secret: '',
    webhook_url: '',
    is_active: true
  });

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      const response = await axios.get(`${API}/integrations`);
      setIntegrations(response.data);
    } catch (error) {
      console.error('Failed to fetch integrations:', error);
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIntegration = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/integrations`, formData);
      toast.success('Integration created successfully');
      setCreateDialogOpen(false);
      setFormData({ name: '', type: 'api', api_key: '', api_secret: '', webhook_url: '', is_active: true });
      fetchIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create integration');
    }
  };

  const handleUpdateIntegration = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/integrations/${selectedIntegration.id}`, formData);
      toast.success('Integration updated successfully');
      setEditDialogOpen(false);
      setSelectedIntegration(null);
      fetchIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update integration');
    }
  };

  const handleDeleteIntegration = async (integrationId) => {
    if (!window.confirm('Are you sure you want to delete this integration?')) return;
    
    try {
      await axios.delete(`${API}/integrations/${integrationId}`);
      toast.success('Integration deleted successfully');
      fetchIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete integration');
    }
  };

  const openEditDialog = (integration) => {
    setSelectedIntegration(integration);
    setFormData({
      name: integration.name,
      type: integration.type,
      api_key: '',
      api_secret: '',
      webhook_url: integration.webhook_url || '',
      is_active: integration.is_active
    });
    setEditDialogOpen(true);
  };

  const getTypeIcon = (type) => {
    const icons = {
      callrail: Phone,
      email: Mail,
      sms: MessageSquare,
      webhook: Webhook,
      api: Plug,
      other: Plug
    };
    const Icon = icons[type] || Plug;
    return <Icon className="h-5 w-5 text-primary" />;
  };

  const IntegrationForm = ({ onSubmit, isEdit = false }) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <Label>Integration Name *</Label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          placeholder="e.g., CallRail Main Account"
          required
        />
      </div>
      <div>
        <Label>Type *</Label>
        <Select value={formData.type} onValueChange={(value) => setFormData({...formData, type: value})}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="callrail">CallRail</SelectItem>
            <SelectItem value="email">Email Provider</SelectItem>
            <SelectItem value="sms">SMS Provider</SelectItem>
            <SelectItem value="webhook">Webhook</SelectItem>
            <SelectItem value="api">API</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>API Key {!isEdit && '*'}</Label>
        <Input
          type="password"
          value={formData.api_key}
          onChange={(e) => setFormData({...formData, api_key: e.target.value})}
          placeholder={isEdit ? 'Leave blank to keep existing' : 'Enter API key'}
          required={!isEdit}
        />
      </div>
      <div>
        <Label>API Secret</Label>
        <Input
          type="password"
          value={formData.api_secret}
          onChange={(e) => setFormData({...formData, api_secret: e.target.value})}
          placeholder={isEdit ? 'Leave blank to keep existing' : 'Enter API secret (optional)'}
        />
      </div>
      <div>
        <Label>Webhook URL</Label>
        <Input
          value={formData.webhook_url}
          onChange={(e) => setFormData({...formData, webhook_url: e.target.value})}
          placeholder="https://example.com/webhook"
        />
      </div>
      <div className="flex items-center space-x-2">
        <Switch
          id="is_active"
          checked={formData.is_active}
          onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
        />
        <Label htmlFor="is_active" className="cursor-pointer">Active</Label>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => { setCreateDialogOpen(false); setEditDialogOpen(false); }}>Cancel</Button>
        <Button type="submit">{isEdit ? 'Update' : 'Create'} Integration</Button>
      </DialogFooter>
    </form>
  );

  const IntegrationCard = ({ integration }) => (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              {getTypeIcon(integration.type)}
              <CardTitle className="text-lg">{integration.name}</CardTitle>
            </div>
            <div className="flex items-center space-x-2 mt-2">
              <Badge variant="outline" className="capitalize">{integration.type}</Badge>
              {integration.is_active ? (
                <Badge className="bg-green-600">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Active
                </Badge>
              ) : (
                <Badge variant="destructive">
                  <XCircle className="h-3 w-3 mr-1" />
                  Inactive
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 mb-4">
          <div className="text-sm">
            <span className="text-muted-foreground">API Key:</span>
            <span className="ml-2 font-mono text-xs">{integration.api_key || 'Not set'}</span>
          </div>
          {integration.webhook_url && (
            <div className="text-sm">
              <span className="text-muted-foreground">Webhook:</span>
              <span className="ml-2 font-mono text-xs truncate block">{integration.webhook_url}</span>
            </div>
          )}
          <div className="text-sm">
            <span className="text-muted-foreground">Status:</span>
            <span className="ml-2 capitalize">{integration.status || 'Not tested'}</span>
          </div>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditDialog(integration)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleDeleteIntegration(integration.id)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/settings')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Integrations</h1>
              <p className="text-muted-foreground">Manage external service integrations</p>
            </div>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Integration
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Integration</DialogTitle>
                <DialogDescription>Connect an external service</DialogDescription>
              </DialogHeader>
              <IntegrationForm onSubmit={handleCreateIntegration} />
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading integrations...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Hover Integration - Featured */}
            <div>
              <h2 className="text-lg font-semibold mb-3">Featured Integrations</h2>
              <HoverIntegrationCard />
            </div>
            
            {/* Other Integrations */}
            <div>
              <h2 className="text-lg font-semibold mb-3">Custom Integrations</h2>
              {integrations.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-8">
                    <Plug className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <h3 className="text-base font-medium mb-2">No custom integrations</h3>
                    <p className="text-sm text-muted-foreground mb-4">Add API keys and webhooks for other services</p>
                    <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Integration
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {integrations.map(integration => <IntegrationCard key={integration.id} integration={integration} />)}
                </div>
              )}
            </div>
          </div>
        )}
        )}

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Integration</DialogTitle>
              <DialogDescription>Update integration settings</DialogDescription>
            </DialogHeader>
            <IntegrationForm onSubmit={handleUpdateIntegration} isEdit />
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};